// Toernooien binnen de organisatie. Een organisatie (club, bond of losse
// organisator) heeft een of meer toernooien; een toernooi heeft edities (de
// events). Een nieuw toernooi maak je hier aan, zonder aanvraag: de
// organisatie bestaat al en heeft haar voorwaarden al aanvaard.
import { getClient } from '../data/supabase.js';
import { state } from '../state.js';
import { heeft } from '../router.js';
import { t, locale } from '../lib/i18n.js';
import { tabelTeken } from '../ui/tabel.js';
import { esc } from '../lib/format.js';
import { toast } from '../ui/toast.js';
import { refLabel } from '../ui/ref.js';

const sb = () => getClient();

export function toernooiFout(e) {
  const m = String(e?.message || e || '').match(/toernooi:([a-z_]+)/);
  return m ? t('toernooi.fout.' + m[1]) : t('alg.fout', { melding: e?.message || String(e) });
}

export async function laad() {
  const { data, error } = await sb().from('tournaments').select('*')
    .eq('organisation_id', state.currentOrgId).is('archived_at', null).order('naam');
  state.tournaments = error ? [] : (data || []);
  return state.tournaments;
}

function formulier(el) {
  if (!el) return;
  el.innerHTML = `<div class="form-row">
      <div class="form-group"><label class="form-label">${esc(t('toernooi.veld.naam'))}</label><input class="form-input" name="tnaam" placeholder="${esc(t('toernooi.veld.naam.voorbeeld'))}"></div>
      <div class="form-group"><label class="form-label">${esc(t('eigendom.aanvraag.land'))}</label><input class="form-input" name="tland" maxlength="2" placeholder="NL"></div>
    </div>
    <div class="form-row">
      <div class="form-group"><label class="form-label">${esc(t('toernooi.veld.editie'))}</label><input class="form-input" name="teditie" placeholder="${esc(t('toernooi.veld.editie.voorbeeld'))}"></div>
      <div class="form-group"><label class="form-label">${esc(t('toernooi.veld.datum'))}</label><input class="form-input" type="date" name="tdatum"></div>
    </div>
    <p style="font-size:12px;color:var(--text-3)">${esc(t('toernooi.veld.uitleg'))}</p>
    <button type="button" class="btn btn-primary btn-sm" data-toernooi="opslaan"><i class="ti ti-check"></i> ${esc(t('toernooi.aanmaken'))}</button>`;
}

// ── Overzicht: alle toernooien van de organisatie ──────────────────────────
// Eerste pagina van de module Toernooien. Een toernooi openen toont zijn
// edities; een editie openen gaat naar het dashboard van die editie.

let opties = {};
let open = null;   // id van het geopende toernooi

const datum = iso => {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z');
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale(), { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};
const knop = (actie, icoon, tekst, soort = 'btn-default', extra = '') =>
  `<button type="button" class="btn ${soort} btn-sm" data-toernooi="${actie}" ${extra}><i class="ti ti-${icoon}"></i> ${esc(tekst)}</button>`;

export async function render(o = opties) {
  opties = o || {};
  const el = document.getElementById('toernooienInhoud');
  if (!el) return;
  el.innerHTML = '<div class="spinner"></div>';
  const [lijst, ev, act] = await Promise.all([
    laad(),
    sb().from('events').select('*').eq('organisation_id', state.currentOrgId).order('tournament_date', { ascending: false }),
    // Aantal teams per editie; ontbreekt de module activiteiten, dan zonder aantallen.
    sb().from('activiteiten_overzicht').select('event_id, teams').eq('organisation_id', state.currentOrgId).eq('kind', 'toernooi'),
  ]);
  // Nieuwste editie eerst, ook als de database niet sorteert.
  const edities = [...(ev.data || [])].sort((x, y) => String(y.tournament_date || '').localeCompare(String(x.tournament_date || '')));
  const teams = Object.fromEntries((act.error ? [] : act.data || []).map(a => [a.event_id, a.teams]));
  if (open && !lijst.some(x => x.id === open)) open = null;
  el.onclick = klik;
  if (open) return detail(el, lijst.find(x => x.id === open), edities.filter(e => e.tournament_id === open), teams);

  const perToernooi = id => edities.filter(e => e.tournament_id === id);
  const beheer = heeft('settings.manage');
  el.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:16px;flex-wrap:wrap">
      ${beheer ? knop('open', 'plus', t('toernooi.nieuw'), 'btn-primary') : ''}
      <span style="font-size:12px;color:var(--text-3)">${esc(t('toernooi.kaart.uitleg', { organisatie: state.currentOrg?.name || '' }))}</span>
    </div>
    <div id="toernooiNieuw" class="settings-card" hidden></div>
    <div id="toernooiTabel"></div>`;
  tabelTeken('toernooien', 'toernooiTabel', [
    { veld: 'naam', label: t('toernooi.kol.naam'), tekst: x => `${x.naam} ${x.ref || ''}`,
      cel: x => `<strong>${esc(x.naam)}</strong> ${refLabel(x.ref)}` },
    { veld: 'land', label: t('eigendom.aanvraag.land') },
    { veld: 'edities', label: t('toernooi.kol.edities'), uitlijn: 'right', mono: true, filter: false, tekst: x => String(perToernooi(x.id).length) },
    { veld: 'laatste', label: t('toernooi.kol.laatste'), filter: false,
      tekst: x => perToernooi(x.id)[0]?.tournament_date || '',
      cel: x => { const e = perToernooi(x.id)[0]; return e ? `${esc(e.name)} <span class="start-sub">${esc(datum(e.tournament_date))}</span>` : ''; } },
    { veld: 'teams', label: t('toernooi.kol.teams'), uitlijn: 'right', mono: true, filter: false,
      tekst: x => String(perToernooi(x.id).reduce((n, e) => n + Number(teams[e.id] || 0), 0)) },
  ], lijst, {
    zoekLabel: t('toernooi.zoek'),
    leeg: t('toernooi.geen'),
    sorteerVeld: 'naam',
    rijKlik: async x => { open = x.id; await opties.kiesToernooi?.(x.id); render(); },
  });
}

function detail(el, toernooi, edities, teams) {
  el.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px">${knop('terug', 'arrow-left', t('toernooi.terug'), 'btn-ghost')}</div>
    <div class="settings-card">
      <div style="display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap">
        <div style="flex:1;min-width:240px">
          <div style="font-size:18px;font-weight:600">${esc(toernooi.naam)} ${refLabel(toernooi.ref)}</div>
          <div style="font-size:13px;color:var(--text-2);margin-top:4px">${esc([state.currentOrg?.name, toernooi.land].filter(Boolean).join(' · '))}</div>
        </div>
        ${heeft('data.write') ? knop('editie', 'plus', t('toernooi.nieuweEditie'), 'btn-primary', `data-id="${esc(toernooi.id)}"`) : ''}
      </div>
    </div>
    <div id="editieTabel"></div>`;
  tabelTeken('edities', 'editieTabel', [
    { veld: 'name', label: t('toernooi.kol.editie'), tekst: e => `${e.name} ${e.ref || ''}`,
      cel: e => `<strong>${esc(e.name)}</strong> ${refLabel(e.ref)}` },
    { veld: 'tournament_date', label: t('toernooi.kol.datum'), filter: false, cel: e => esc(datum(e.tournament_date)) },
    { veld: 'location', label: t('toernooi.kol.locatie') },
    { veld: 'teams', label: t('toernooi.kol.teams'), uitlijn: 'right', mono: true, filter: false, tekst: e => String(teams[e.id] ?? '') },
  ], edities, {
    zoekLabel: t('toernooi.zoek.editie'),
    leeg: t('toernooi.edities.leeg'),
    rijKlik: e => opties.openEditie?.(e.id),
  });
}

async function klik(e) {
  const k = e.target.closest('[data-toernooi]');
  if (!k) return;
  const a = k.dataset.toernooi;
  if (a === 'terug') { open = null; await opties.kiesToernooi?.(null); await render(); }
  if (a === 'editie') opties.nieuweEditie?.(k.dataset.id);
  if (a === 'open') {
    const vak = document.getElementById('toernooiNieuw');
    vak.hidden = false;
    formulier(vak);
  }
  if (a === 'opslaan') {
    const el = document.getElementById('toernooienInhoud');
    const w = n => el.querySelector(`[name="${n}"]`)?.value || '';
    const { data, error } = await sb().rpc('toernooi_aanmaken', {
      p_org: state.currentOrgId, p_naam: w('tnaam'), p_land: w('tland') || null,
      p_editie: w('teditie') || null, p_datum: w('tdatum') || null,
    });
    if (error) { toast(toernooiFout(error), 'error'); return; }
    toast(t('toernooi.aangemaakt'), 'success');
    if (opties.naAanmaken) await opties.naAanmaken();
    if (data) { open = data; await opties.kiesToernooi?.(data); }
    await render();
  }
}

export async function vernieuw() { await render(); }
export function zetOpen(id) { open = id || null; }
